import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, catchError, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';

/** Environment */
import { environment } from '../../environments/environment';

/** Models */
import { PagedList } from '../models/common/paged-list';
import {
  ApiAccountingPeriodFormat,
  ApiConfigurationKey,
  ApiOrganizationAddress,
  ApiOrganizationConfiguration,
  ApiOrganizationConfigurationRequest,
  ApiOrganizationIntegrations,
  ApiOrganizationPlatformRequest,
  ApiOrganizationCreateRequest,
  ApiOrganizationDetail,
  ApiOrganizationListItem,
  ApiOrganizationModule,
  ApiOrganizationModuleState,
  ApiOrganizationNetSuiteRequest,
  ApiOrganizationSync,
  ApiOrganizationSearchRequest,
  ApiOrganizationStatusRequest,
  ApiOrganizationProductConfiguration,
  ApiOrganizationResponse,
  ApiOrganizationUpdateRequest,
  ApiPlatformType,
} from '../models/request/organization-api.model';
import {
  AccountingPeriodFormat,
  AccountingSoftware,
  DEFAULT_ORGANIZATION_CONFIGURATIONS,
  DedicatedEmail,
  DedicatedEmailStatus,
  EmailToApDraftActor,
  OrganizationAccountingIntegration,
  OrganizationAddress,
  OrganizationBasicDetailsUpdate,
  OrganizationConfigurations,
  OrganizationDetail,
  OrganizationModuleKey,
  OrganizationModuleState,
  NetSuiteCredentials,
  NetSuiteConnectionTestResult,
  PhoneNumberValue,
  OrganizationCreateRequest,
  OrganizationListItem,
  OrganizationSearchRequest,
  resolveEmailProvider,
} from '../models/organization.model';
import { SUPPRESS_ERROR_TOAST_HEADER } from '../utils/api-error.utils';

/** Services */
import { CompanyService } from './company.service';

const LIST_ORDER_KEY = 'organization-list-context';

/**
 * The listing page the detail page was opened from, for "3 of 16 in All Organizations" and the
 * up/down arrows (AC 6.13), which move on to the next or previous page at the edges.
 */
export interface OrganizationListContext {
  /** Ids of the rows on the current page, in order. */
  ids: string[];
  /** The search that produced them (page, size, keyword, filters). */
  request: OrganizationSearchRequest;
  /** All matching organizations, across pages. */
  totalRecords: number;
}

/** Wizard value → API `AccountingPeriodFormat`. */
const PERIOD_FORMATS: Record<AccountingPeriodFormat, ApiAccountingPeriodFormat> = {
  [AccountingPeriodFormat.CalendarMonths]: 'CalendarMonths',
  [AccountingPeriodFormat.FourWeeks]: 'FourWeeks',
  [AccountingPeriodFormat.FourFourFiveWeeks]: 'FourFourFive',
};

/** Wizard value → API `PlatformType`. */
const PLATFORM_TYPES: Record<AccountingSoftware, ApiPlatformType> = {
  [AccountingSoftware.NetSuite]: 'NetSuite',
  [AccountingSoftware.QuickBooks]: 'QuickBook',
  [AccountingSoftware.Xero]: 'Xero',
};

/** Wizard module → API `OrganizationModule`. */
const API_MODULES: Record<OrganizationModuleKey, ApiOrganizationModule> = {
  [OrganizationModuleKey.AccountsPayable]: 'AccountsPayable',
  [OrganizationModuleKey.ExpenseManagement]: 'ExpenseManagement',
  [OrganizationModuleKey.RepairAndMaintenance]: 'RepairAndMaintenance',
  [OrganizationModuleKey.MealDynamicsAi]: 'Ai',
};

const MODULE_KEYS = Object.fromEntries(
  Object.entries(API_MODULES).map(([key, api]) => [api, key])
) as Record<ApiOrganizationModule, OrganizationModuleKey>;

/** Modules that have a "Sync … to Accounts Payable" toggle → API `OrganizationSync`. */
const API_SYNCS: Partial<Record<OrganizationModuleKey, ApiOrganizationSync>> = {
  [OrganizationModuleKey.ExpenseManagement]: 'ExpensesToAccountsPayable',
  [OrganizationModuleKey.RepairAndMaintenance]: 'RepairAndMaintenanceToAccountsPayable',
};

/** Additional Configurations field → API `ConfigurationKey`. */
const CONFIGURATION_KEYS: Record<keyof OrganizationConfigurations, ApiConfigurationKey> = {
  bypassApManager1: 'ByPassApManager1',
  vendorInviteExpiryDays: 'VendorInviteExpiry',
  externalVendorInviteExpiryDays: 'VendorInviteExpiryInDays',
  vendorPaymentDetailsExpiryDays: 'VendorPaymentDetailRequestExpiry',
  createEmailToApDraft: 'IsEmailToApDraftEnabled',
  allowMealDynamicsAi: 'IsAiModuleEnabled',
  implementRolesAndPermissions: 'RolesAndPermissions',
};

/** `additionalConfigurations` from the GET → the section's values (toggles read isEnabled, day fields read days). */
function toConfigurations(items: ApiOrganizationConfiguration[] | null | undefined): OrganizationConfigurations {
  const result: OrganizationConfigurations = { ...DEFAULT_ORGANIZATION_CONFIGURATIONS };
  (Object.keys(CONFIGURATION_KEYS) as (keyof OrganizationConfigurations)[]).forEach((field) => {
    const item = items?.find((x) => x.key === CONFIGURATION_KEYS[field]);
    if (!item) return;
    if (typeof DEFAULT_ORGANIZATION_CONFIGURATIONS[field] === 'boolean') {
      (result as any)[field] = !!item.isEnabled;
    } else {
      (result as any)[field] = item.days ?? null;
    }
  });
  return result;
}

/** The organization's dedicated emails with their request status (AC 6.33: a pending link past its expiry is expired). */
function toDedicatedEmails(integrations: ApiOrganizationIntegrations | null | undefined): DedicatedEmail[] {
  return (integrations?.dedicatedEmails ?? [])
    .filter((mailbox) => !!mailbox?.email)
    .map((mailbox) => {
      const email = mailbox.email!;
      let status: DedicatedEmailStatus | undefined;
      if (mailbox.status === 'Connected') {
        status = DedicatedEmailStatus.Connected;
      } else if (mailbox.status === 'Pending') {
        const expiresOn = mailbox.inviteExpiresOn ? Date.parse(mailbox.inviteExpiresOn) : NaN;
        status = !isNaN(expiresOn) && expiresOn < Date.now()
          ? DedicatedEmailStatus.RequestExpired
          : DedicatedEmailStatus.PendingConfirmation;
      }
      return { id: mailbox.id, email, provider: resolveEmailProvider(email), status };
    });
}

/** The user email invoices are created for; null when the API names nobody. */
export function toEmailToApDraftActor(integrations: ApiOrganizationIntegrations | null | undefined): EmailToApDraftActor | null {
  const actor = integrations?.emailToApDraftActor;
  return actor?.userId ? { userId: actor.userId, name: actor.name?.trim() || actor.email || '', email: actor.email ?? '' } : null;
}

/** API module names → what the listing chips show. */
const MODULE_NAMES: Record<ApiOrganizationModule, string> = {
  AccountsPayable: 'Accounts Payable',
  ExpenseManagement: 'Expense Management',
  RepairAndMaintenance: 'Repair & Maintenance',
  Ai: 'Meal Dynamics AI',
};

/**
 * Chip label for an API module name. Matched ignoring case, since the listing has sent "AI" for
 * `Ai`, which then showed as "AI" instead of "Meal Dynamics AI".
 */
function moduleDisplayName(module: string): string {
  const key = (Object.keys(MODULE_NAMES) as ApiOrganizationModule[]).find(
    (k) => k.toLowerCase() === (module ?? '').toLowerCase()
  );
  return key ? MODULE_NAMES[key] : module;
}

/** API `PlatformType` → wizard value. */
const SOFTWARE_BY_PLATFORM: Record<ApiPlatformType, AccountingSoftware | null> = {
  None: null,
  NetSuite: AccountingSoftware.NetSuite,
  QuickBook: AccountingSoftware.QuickBooks,
  Xero: AccountingSoftware.Xero,
};

/** The API stores a bare phone number; the UI shows it with the US code. */
function toPhoneValue(phone: string | null | undefined): PhoneNumberValue | null {
  return phone ? { countryCode: 'US', dialCode: '1', number: phone } : null;
}

/**
 * Organization (tenant) API for the System Admin portal.
 *
 * <p>Everything goes through `/v1/Company/organization` (search, create, detail, update, status,
 * delete). See the TODOs for what the API doesn't cover yet.</p>
 */
@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly organizationUrl = `${environment.apiUrl}/v1/Company/organization`;

  private readonly listChangedSource = new Subject<void>();

  /**
   * Emits after an organization is created, deleted, deactivated or reactivated, so lists kept
   * elsewhere (the header organization picker) can reload without a page refresh.
   */
  readonly listChanged$ = this.listChangedSource.asObservable();

  constructor(private http: HttpClient, private companySvc: CompanyService) {}

  public uploadLogoAsync(file: File): Observable<string> {
    const fd = new FormData();
    fd.append('File', file);
    return this.companySvc.uploadLogoAsync(fd).pipe(map((x) => x.uri));
  }

  /** Last loaded organization per id; PUT sends the whole organization (see `put`). */
  private readonly detailCache = new Map<string, ApiOrganizationDetail>();

  /** AC 5.1, 5.2: creates the organization, its admin, product configuration and integrations. */
  public createAsync(request: OrganizationCreateRequest): Observable<ApiOrganizationResponse> {
    return this.http
      .post<ApiOrganizationResponse>(this.organizationUrl, this.toCreateRequest(request))
      .pipe(tap(() => this.listChangedSource.next()));
  }

  /** AC 6.1–6.4: `POST /v1/Company/organization/search`. */
  public searchAsync(request: OrganizationSearchRequest): Observable<PagedList<OrganizationListItem>> {
    const payload: ApiOrganizationSearchRequest = {
      page: request.page,
      pageSize: request.pageSize,
      accountingSoftware: request.accountingSoftware ? PLATFORM_TYPES[request.accountingSoftware] : null,
      // AC 6.5, 6.6: active only by default; "View Inactive Organizations" lists all of them.
      isActive: request.includeInactive ? null : true,
      ...(request.keyword ? { keyword: request.keyword } : {}),
    };

    return this.http.post<PagedList<ApiOrganizationListItem>>(`${this.organizationUrl}/search`, payload).pipe(
      map((paged) => ({ ...paged, items: (paged.items ?? []).map((x) => this.toListItem(x)) }))
    );
  }

  /** AC 6.10: users of the organization can't sign in until it's reactivated. */
  public deactivateAsync(id: string): Observable<void> {
    return this.setStatusAsync(id, false);
  }

  /** AC 6.11 */
  public reactivateAsync(id: string): Observable<void> {
    return this.setStatusAsync(id, true);
  }

  /** AC 6.12: the backend keeps the data for 90 days. */
  public deleteAsync(id: string): Observable<unknown> {
    return this.http.delete(`${this.organizationUrl}/${id}`).pipe(tap(() => this.listChangedSource.next()));
  }

  private setStatusAsync(id: string, isActive: boolean): Observable<void> {
    const body: ApiOrganizationStatusRequest = { isActive };
    return this.http.put(`${this.organizationUrl}/${id}/status`, body).pipe(
      tap(() => this.listChangedSource.next()),
      map(() => undefined)
    );
  }

  /** Order of the rows last shown in the listing, for the detail page's up/down navigation (AC 6.13). */
  public rememberListContext(context: OrganizationListContext): void {
    try {
      sessionStorage.setItem(LIST_ORDER_KEY, JSON.stringify(context));
    } catch {
      // Navigation just hides without storage.
    }
  }

  public getListContext(): OrganizationListContext | null {
    try {
      const ctx = JSON.parse(sessionStorage.getItem(LIST_ORDER_KEY) ?? 'null');
      return ctx && Array.isArray(ctx.ids) && ctx.request ? (ctx as OrganizationListContext) : null;
    } catch {
      return null;
    }
  }

  // ── Detail ────────────────────────────────────────────────────────────────

  public getByIdAsync(id: string): Observable<OrganizationDetail> {
    return this.http
      .get<ApiOrganizationDetail>(`${this.organizationUrl}/${id}`)
      .pipe(
        tap((detail) => this.detailCache.set(id, detail)),
        map((detail) => this.toDetail(detail))
      );
  }

  public updateBasicDetailsAsync(id: string, update: OrganizationBasicDetailsUpdate): Observable<void> {
    return this.put(id, (req) => {
      req.website = update.website || null;
      req.country = update.country || null;
      req.email = update.email || null;
      req.phone = update.phone?.number || null;
      req.timeZone = update.timeZone || null;
    });
  }

  /** Organization name, edited from the detail page header. */
  public updateNameAsync(id: string, name: string): Observable<void> {
    return this.put(id, (req) => {
      req.name = name;
    }).pipe(tap(() => this.listChangedSource.next()));
  }

  /** Product Configurations › Accounting period format; the rest of the product configuration is kept. */
  public updateAccountingPeriodFormatAsync(id: string, format: AccountingPeriodFormat | null): Observable<void> {
    return this.put(id, (req) => {
      // The GET always carries productConfiguration; module flags in it are left as they are.
      if (req.productConfiguration) {
        req.productConfiguration = { ...req.productConfiguration, accountingPeriodFormat: format ? PERIOD_FORMATS[format] : 'None' };
      }
    });
  }

  private fromApiPeriodFormat(value: ApiAccountingPeriodFormat | null | undefined): AccountingPeriodFormat | null {
    const match = (Object.keys(PERIOD_FORMATS) as AccountingPeriodFormat[]).find((key) => PERIOD_FORMATS[key] === value);
    return match ?? null;
  }

  /** Organization photo, changed from the detail page header (upload it first with uploadLogoAsync). */
  public updateLogoAsync(id: string, logoUrl: string | null): Observable<void> {
    return this.put(id, (req) => {
      req.logoUrl = logoUrl;
    }).pipe(tap(() => this.listChangedSource.next()));
  }

  public updateAddressAsync(id: string, address: OrganizationAddress): Observable<void> {
    return this.put(id, (req) => {
      req.address = this.toApiAddress(address);
    });
  }

  /** AC 6.25: `POST …/modules/{module}` for each module picked in the Modules Center. */
  public addModulesAsync(id: string, keys: OrganizationModuleKey[]): Observable<void> {
    if (!keys.length) return of(undefined);
    return forkJoin(
      keys.map((key) => this.http.post(`${this.organizationUrl}/${id}/modules/${API_MODULES[key]}`, {}))
    ).pipe(this.afterPartialChange(id));
  }

  /** AC 6.21, 6.23: `PUT …/modules/{module}/status`. */
  public setModuleStatusAsync(id: string, key: OrganizationModuleKey, isActive: boolean): Observable<void> {
    const body: ApiOrganizationStatusRequest = { isActive };
    return this.http
      .put(`${this.organizationUrl}/${id}/modules/${API_MODULES[key]}/status`, body)
      .pipe(this.afterPartialChange(id));
  }

  /** AC 6.19: `PUT …/syncs/{sync}/status` for the "Sync … to Accounts Payable" toggles. */
  public setSyncStatusAsync(id: string, key: OrganizationModuleKey, isActive: boolean): Observable<void> {
    const sync = API_SYNCS[key];
    if (!sync) return of(undefined);
    const body: ApiOrganizationStatusRequest = { isActive };
    return this.http.put(`${this.organizationUrl}/${id}/syncs/${sync}/status`, body).pipe(this.afterPartialChange(id));
  }

  /** AC 6.24: `DELETE …/modules/{module}`; the backend keeps the data for 90 days. */
  public removeModuleAsync(id: string, key: OrganizationModuleKey): Observable<void> {
    return this.http.delete(`${this.organizationUrl}/${id}/modules/${API_MODULES[key]}`).pipe(this.afterPartialChange(id));
  }

  /**
   * After a change made outside the full update, the cached organization is stale; drop it so the
   * next full update starts from a fresh GET instead of sending old module flags back.
   */
  private afterPartialChange(id: string) {
    return (source: Observable<unknown>): Observable<void> =>
      source.pipe(
        tap(() => this.detailCache.delete(id)),
        map(() => undefined)
      );
  }

  /**
   * AC 6.26: `PUT …/configurations/{key}` for every setting that changed. Each one is saved on its own,
   * so one failing doesn't drop the others; returns the settings that were saved.
   */
  public updateConfigurationsAsync(
    id: string,
    next: OrganizationConfigurations,
    previous: OrganizationConfigurations
  ): Observable<(keyof OrganizationConfigurations)[]> {
    const changed = (Object.keys(CONFIGURATION_KEYS) as (keyof OrganizationConfigurations)[]).filter(
      (field) => next[field] !== previous[field]
    );
    if (!changed.length) return of([]);

    return forkJoin(
      changed.map((field) => {
        const value = next[field];
        const body: ApiOrganizationConfigurationRequest =
          typeof value === 'boolean'
            ? { isEnabled: value, days: null }
            : { isEnabled: value != null, days: value ?? null };
        return this.http
          .put(`${this.organizationUrl}/${id}/configurations/${CONFIGURATION_KEYS[field]}`, body)
          .pipe(
            map(() => field),
            // The interceptor already shows the error; report the setting as not saved.
            catchError(() => of(null))
          );
      })
    ).pipe(
      tap(() => this.detailCache.delete(id)),
      map((results) => results.filter((x): x is keyof OrganizationConfigurations => x !== null))
    );
  }

  /** AC 6.27, 6.31: `POST …/platform`. */
  /**
   * Test Connection: reads this year's accounting periods from NetSuite with these credentials; nothing is saved.
   * Blank fields (and masked secrets) fall back to the stored values. Failures are shown inline, so no error toast.
   */
  public testNetSuiteConnectionAsync(ns: Partial<NetSuiteCredentials>): Observable<NetSuiteConnectionTestResult> {
    const value = (x: string | null | undefined) => {
      const trimmed = (x ?? '').trim();
      // The API masks stored secrets (e.g. "****e918"); send those blank so the stored value is used.
      return trimmed && !/^\*{3,}/.test(trimmed) ? trimmed : null;
    };
    const body = {
      baseUrl: value(ns.baseUrl),
      signatureMethod: value(ns.signatureMethod),
      consumerKey: value(ns.consumerKey),
      consumerSecret: value(ns.consumerSecret),
      accessToken: value(ns.accessToken),
      tokenSecret: value(ns.tokenSecret),
      realm: value(ns.realm),
    };
    return this.http.post<NetSuiteConnectionTestResult>(`${this.organizationUrl}/netsuite/test`, body, {
      headers: new HttpHeaders({ [SUPPRESS_ERROR_TOAST_HEADER]: 'true' }),
    });
  }

  public saveAccountingIntegrationAsync(id: string, integration: OrganizationAccountingIntegration): Observable<void> {
    const body: ApiOrganizationPlatformRequest = {
      accountingSoftware: PLATFORM_TYPES[integration.software],
      netSuite: integration.netSuite ? this.toApiNetSuite(integration.netSuite) : null,
    };
    return this.http.post(`${this.organizationUrl}/${id}/platform`, body).pipe(this.afterPartialChange(id));
  }

  /** AC 6.30: `DELETE …/platform` disconnects and clears the stored credentials. */
  public removeAccountingIntegrationAsync(id: string): Observable<void> {
    return this.http.delete(`${this.organizationUrl}/${id}/platform`).pipe(this.afterPartialChange(id));
  }

  /**
   * AC 6.32: `POST …/dedicated-emails` adds the mailbox and sends its integration request right away.
   * Returns the organization's emails as they are now.
   */
  public addDedicatedEmailAsync(id: string, email: string): Observable<DedicatedEmail[]> {
    return this.http
      .post<ApiOrganizationIntegrations>(`${this.organizationUrl}/${id}/dedicated-emails`, { email })
      .pipe(
        tap(() => this.detailCache.delete(id)),
        map((res) => toDedicatedEmails(res))
      );
  }

  /** AC 6.34: `POST …/dedicated-emails/{mailboxId}/resend` sends a new link and invalidates the old one. */
  public resendDedicatedEmailAsync(id: string, email: DedicatedEmail): Observable<DedicatedEmail[]> {
    return this.http
      .post<ApiOrganizationIntegrations>(`${this.organizationUrl}/${id}/dedicated-emails/${email.id}/resend`, {})
      .pipe(
        tap(() => this.detailCache.delete(id)),
        map((res) => toDedicatedEmails(res))
      );
  }

  /** AC 6.37, 6.38: `DELETE …/dedicated-emails/{mailboxId}`. */
  public removeDedicatedEmailAsync(id: string, email: DedicatedEmail): Observable<void> {
    return this.http
      .delete(`${this.organizationUrl}/${id}/dedicated-emails/${email.id}`)
      .pipe(this.afterPartialChange(id));
  }

  /**
   * `PUT /v1/Company/organization/{id}` takes the whole organization, so every change is sent as the
   * last loaded state with that change applied. The response becomes the new state.
   */
  private put(id: string, change: (req: ApiOrganizationUpdateRequest) => void): Observable<void> {
    const current$ = this.detailCache.has(id)
      ? of(this.detailCache.get(id)!)
      : this.http.get<ApiOrganizationDetail>(`${this.organizationUrl}/${id}`);

    return current$.pipe(
      switchMap((current) => {
        const req = this.toUpdateRequest(current);
        change(req);
        return this.http.put<ApiOrganizationDetail>(`${this.organizationUrl}/${id}`, req);
      }),
      tap((updated) => {
        // With no body back, drop the cache so the next PUT re-reads the organization instead of
        // resending stale values (e.g. an older logo over the one just saved).
        if (updated?.company) this.detailCache.set(id, updated);
        else this.detailCache.delete(id);
      }),
      catchError((error) => {
        this.detailCache.delete(id);
        return throwError(() => error);
      }),
      map(() => undefined)
    );
  }

  private toUpdateRequest(d: ApiOrganizationDetail): ApiOrganizationUpdateRequest {
    const c = d.company ?? ({} as ApiOrganizationDetail['company']);
    const ns = d.integrations?.netSuite;
    return {
      name: c.name ?? null,
      timeZone: c.timeZone ?? null,
      country: c.country ?? null,
      website: c.website ?? null,
      email: c.contactEmail ?? null,
      phone: c.phone ?? null,
      logoUrl: c.logoUrl ?? null,
      admin: d.admin
        ? { firstName: d.admin.firstName, lastName: d.admin.lastName, email: d.admin.email, phone: d.admin.phone }
        : null,
      address: d.address ? { ...d.address } : null,
      productConfiguration: d.productConfiguration ? { ...d.productConfiguration } : null,
      integrations: d.integrations
        ? {
            accountingSoftware: d.integrations.accountingSoftware,
            // Sent back as loaded so other edits don't touch the credentials.
            netSuite:
              ns && ns.isConfigured
                ? {
                    baseUrl: ns.baseUrl ?? '',
                    signatureMethod: ns.signatureMethod ?? '',
                    consumerKey: ns.consumerKey ?? '',
                    consumerSecret: ns.consumerSecret ?? '',
                    accessToken: ns.accessToken ?? '',
                    tokenSecret: ns.tokenSecret ?? '',
                    realm: ns.realm ?? '',
                  }
                : null,
            dedicatedEmail: null,
          }
        : null,
    };
  }

  private toDetail(d: ApiOrganizationDetail): OrganizationDetail {
    const c = d.company ?? ({} as ApiOrganizationDetail['company']);
    const pc = d.productConfiguration;
    const software = SOFTWARE_BY_PLATFORM[d.integrations?.accountingSoftware ?? 'None'] ?? null;
    const ns = d.integrations?.netSuite;
    const adminName = [d.admin?.firstName, d.admin?.lastName].filter(Boolean).join(' ');

    return {
      id: d.companyId ?? c.id,
      name: c.name,
      logoUrl: c.logoUrl || null,
      createdOn: c.createdOn,
      isActive: d.isActive ?? true,
      website: c.website || null,
      country: c.country || null,
      email: c.contactEmail || null,
      phone: toPhoneValue(c.phone),
      timeZone: c.timeZone || null,
      admin: d.admin && adminName ? { userId: d.admin.userId ?? null, name: adminName, avatarUrl: null } : null,
      address: {
        addressLine1: d.address?.addressLine1 ?? null,
        addressLine2: d.address?.addressLine2 ?? null,
        country: d.address?.country ?? null,
        state: d.address?.state ?? null,
        city: d.address?.city ?? null,
        zip: d.address?.zip ?? null,
        latitude: d.address?.latitude ?? null,
        longitude: d.address?.longitude ?? null,
      },
      modules: d.modules?.length ? this.fromApiModuleStates(d.modules, pc) : pc ? this.fromApiModules(pc) : [],
      accountingPeriodFormat: this.fromApiPeriodFormat(pc?.accountingPeriodFormat),
      configurations: toConfigurations(d.additionalConfigurations),
      accountingIntegration: software
        ? {
            software,
            netSuite:
              software === AccountingSoftware.NetSuite && ns
                ? {
                    baseUrl: ns.baseUrl ?? '',
                    signatureMethod: ns.signatureMethod ?? '',
                    consumerKey: ns.consumerKey ?? '',
                    consumerSecret: ns.consumerSecret ?? '',
                    tokenSecret: ns.tokenSecret ?? '',
                    accessToken: ns.accessToken ?? '',
                    realm: ns.realm ?? '',
                  }
                : undefined,
          }
        : null,
      dedicatedEmails: toDedicatedEmails(d.integrations),
      emailToApDraftActor: toEmailToApDraftActor(d.integrations),
    };
  }

  private fromApiModules(pc: ApiOrganizationProductConfiguration): OrganizationModuleState[] {
    const modules: OrganizationModuleState[] = [];
    const add = (key: OrganizationModuleKey, enabled: boolean, sync = false) => {
      if (enabled) modules.push({ key, isActive: true, syncToAccountsPayable: sync });
    };
    add(OrganizationModuleKey.AccountsPayable, pc.isAccountsPayableEnabled);
    add(OrganizationModuleKey.ExpenseManagement, pc.isExpenseManagementEnabled, pc.syncExpensesToAccountsPayable);
    add(OrganizationModuleKey.RepairAndMaintenance, pc.isRepairAndMaintenanceEnabled, pc.syncRepairAndMaintenanceToAccountsPayable);
    add(OrganizationModuleKey.MealDynamicsAi, pc.isAiEnabled);
    return modules;
  }

  /** AC 6.19: Active and Inactive modules are listed; Removed ones are not. */
  private fromApiModuleStates(
    states: ApiOrganizationModuleState[],
    pc: ApiOrganizationProductConfiguration | null
  ): OrganizationModuleState[] {
    return states
      .filter((x) => x.status !== 'Removed')
      .map((x) => {
        const key = MODULE_KEYS[x.module];
        return {
          key,
          isActive: x.status === 'Active',
          syncToAccountsPayable:
            key === OrganizationModuleKey.ExpenseManagement
              ? !!pc?.syncExpensesToAccountsPayable
              : key === OrganizationModuleKey.RepairAndMaintenance
                ? !!pc?.syncRepairAndMaintenanceToAccountsPayable
                : false,
        };
      })
      .filter((x) => !!x.key);
  }

  /** Modules on screen → product configuration flags, keeping the period format and platform. */
  private toApiModules(
    modules: OrganizationModuleState[],
    current: ApiOrganizationProductConfiguration | null
  ): ApiOrganizationProductConfiguration {
    const find = (key: OrganizationModuleKey) => modules.find((m) => m.key === key);
    const ap = !!find(OrganizationModuleKey.AccountsPayable);
    const expense = find(OrganizationModuleKey.ExpenseManagement);
    const repair = find(OrganizationModuleKey.RepairAndMaintenance);
    return {
      isAccountsPayableEnabled: ap,
      isExpenseManagementEnabled: !!expense,
      syncExpensesToAccountsPayable: ap && !!expense?.syncToAccountsPayable,
      isRepairAndMaintenanceEnabled: !!repair,
      syncRepairAndMaintenanceToAccountsPayable: ap && !!repair?.syncToAccountsPayable,
      isAiEnabled: !!find(OrganizationModuleKey.MealDynamicsAi),
      accountingPeriodFormat: current?.accountingPeriodFormat ?? 'None',
      platform: current?.platform ?? 'None',
    };
  }

  private toApiAddress(address: OrganizationAddress | null | undefined): ApiOrganizationAddress | null {
    if (!address) return null;
    return {
      addressLine1: address.addressLine1 ?? null,
      addressLine2: address.addressLine2 ?? null,
      country: address.country ?? null,
      state: address.state ?? null,
      city: address.city ?? null,
      zip: address.zip ?? null,
      latitude: address.latitude ?? 0,
      longitude: address.longitude ?? 0,
    };
  }

  private toApiNetSuite(ns: NetSuiteCredentials): ApiOrganizationNetSuiteRequest {
    return {
      baseUrl: ns.baseUrl.trim(),
      signatureMethod: ns.signatureMethod,
      consumerKey: ns.consumerKey.trim(),
      consumerSecret: ns.consumerSecret.trim(),
      accessToken: ns.accessToken.trim(),
      tokenSecret: ns.tokenSecret.trim(),
      realm: ns.realm.trim(),
    };
  }

  private toListItem(x: ApiOrganizationListItem): OrganizationListItem {
    return {
      id: x.companyId,
      name: x.name ?? '',
      logoUrl: x.logoUrl || null,
      adminName: x.adminName || null,
      adminAvatarUrl: x.adminProfilePictureUrl || null,
      accountingSoftware: SOFTWARE_BY_PLATFORM[x.accountingSoftware] ?? null,
      modules: (x.modules ?? []).map((m) => moduleDisplayName(m)).filter(Boolean),
      isActive: x.isActive,
    };
  }

  private toCreateRequest(request: OrganizationCreateRequest): ApiOrganizationCreateRequest {
    const integration = request.accountingIntegration;
    const platform: ApiPlatformType = integration ? PLATFORM_TYPES[integration.software] : 'None';
    const ns = integration?.software === AccountingSoftware.NetSuite ? integration.netSuite : undefined;

    return {
      name: request.name?.trim(),
      timeZone: request.timeZone,
      country: request.country || null,
      website: request.website || null,
      email: request.email || null,
      // The API takes one phone string with no country code; send the number as the user forms do.
      phone: request.phone?.number || null,
      logoUrl: request.logoUrl || null,
      admin: {
        firstName: request.admin.firstName,
        lastName: request.admin.lastName,
        email: request.admin.email,
        phone: request.admin.phone.number,
        password: request.admin.password,
        confirmPassword: request.admin.password,
      },
      address: this.toApiAddress(request.address),
      productConfiguration: {
        ...this.toApiModules(
          request.modules.map((m) => ({ key: m.key, isActive: true, syncToAccountsPayable: m.syncToAccountsPayable })),
          null
        ),
        accountingPeriodFormat: request.accountingPeriodFormat
          ? PERIOD_FORMATS[request.accountingPeriodFormat]
          : 'None',
        platform,
      },
      integrations: {
        accountingSoftware: platform,
        netSuite: ns ? this.toApiNetSuite(ns) : null,
        // Sent afterwards, one `POST …/dedicated-emails` per email, once the organization exists (AC 5.2).
        dedicatedEmail: null,
      },
    };
  }
}
